import React, { useEffect, useState } from "react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const TermsOfService = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: [
        {
          subtitle: "Agreement",
          text: "By accessing or using Appraiser Assistant's services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services."
        },
        {
          subtitle: "Eligibility",
          text: "You must be at least 18 years old and have the legal capacity to enter into a binding agreement to use our services."
        }
      ]
    },
    {
      title: "2. Description of Services",
      content: [
        {
          subtitle: "Vehicle Appraisal Services",
          text: "Appraiser Assistant provides vehicle valuation and appraisal tools designed to assist insurance adjusters, appraisers, and other professionals in determining fair market values for vehicles."
        },
        {
          subtitle: "Data-Driven Valuations",
          text: "Our valuations are based on market data, vehicle specifications, condition assessments, and comparable sales. While we strive for accuracy, valuations are estimates and should be used as guidance."
        },
        {
          subtitle: "Service Availability",
          text: "We strive to maintain continuous service availability but do not guarantee uninterrupted access. Services may be temporarily unavailable for maintenance or updates."
        }
      ]
    },
    {
      title: "3. User Accounts",
      content: [
        {
          subtitle: "Account Creation",
          text: "To access certain features, you must create an account with accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials."
        },
        {
          subtitle: "Account Security",
          text: "You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use or security breach."
        },
        {
          subtitle: "Account Termination",
          text: "We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent, abusive, or illegal activities."
        }
      ]
    },
    {
      title: "4. Subscription and Payments",
      content: [
        {
          subtitle: "Subscription Plans",
          text: "Access to premium features requires a paid subscription. Subscription details, pricing, and features are described on our pricing page."
        },
        {
          subtitle: "Billing",
          text: "Subscriptions are billed in advance on a recurring basis. You authorize us to charge your payment method for all applicable fees."
        },
        {
          subtitle: "Refunds",
          text: "Refund requests are handled on a case-by-case basis. Please contact our support team for refund inquiries within 14 days of purchase."
        },
        {
          subtitle: "Price Changes",
          text: "We reserve the right to modify pricing with reasonable notice. Existing subscribers will be notified of any changes before their next billing cycle."
        }
      ]
    },
    {
      title: "5. Acceptable Use",
      content: [
        {
          subtitle: "Permitted Use",
          text: "You may use our services for lawful purposes related to vehicle appraisal and valuation in accordance with these terms."
        },
        {
          subtitle: "Prohibited Activities",
          text: "You may not: (a) use the service for illegal purposes, (b) attempt to gain unauthorized access, (c) interfere with service operation, (d) scrape or harvest data, (e) resell or redistribute our services without permission, or (f) submit false or misleading information."
        }
      ]
    },
    {
      title: "6. Intellectual Property",
      content: [
        {
          subtitle: "Ownership",
          text: "All content, features, and functionality of our services are owned by Appraiser Assistant and protected by intellectual property laws."
        },
        {
          subtitle: "Limited License",
          text: "We grant you a limited, non-exclusive, non-transferable license to access and use our services for their intended purpose."
        },
        {
          subtitle: "User Content",
          text: "You retain ownership of data you submit but grant us a license to use it for service delivery and improvement purposes."
        }
      ]
    },
    {
      title: "7. Disclaimers",
      content: [
        {
          subtitle: "Valuation Estimates",
          text: "Vehicle valuations provided are estimates based on available data and should not be considered as guarantees of actual market value. Final values may vary based on actual vehicle condition and market conditions."
        },
        {
          subtitle: "No Professional Advice",
          text: "Our services do not constitute professional appraisal, legal, financial, or insurance advice. Consult qualified professionals for specific situations."
        },
        {
          subtitle: "Service Provided 'As Is'",
          text: "Services are provided 'as is' without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement."
        }
      ]
    },
    {
      title: "8. Limitation of Liability",
      content: [
        {
          subtitle: "Liability Cap",
          text: "To the maximum extent permitted by law, Appraiser Assistant shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services."
        },
        {
          subtitle: "Maximum Liability",
          text: "Our total liability for any claims arising from these terms or your use of services shall not exceed the amount you paid us in the 12 months preceding the claim."
        }
      ]
    },
    {
      title: "9. Indemnification",
      content: [
        {
          subtitle: "Your Responsibility",
          text: "You agree to indemnify and hold harmless Appraiser Assistant, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of our services or violation of these terms."
        }
      ]
    },
    {
      title: "10. Changes to Terms",
      content: [
        {
          subtitle: "Modifications",
          text: "We may modify these terms at any time. Material changes will be communicated through email or prominent notice on our platform. Continued use after changes constitutes acceptance."
        }
      ]
    },
    {
      title: "11. Governing Law",
      content: [
        {
          subtitle: "Jurisdiction",
          text: "These terms are governed by the laws of the jurisdiction in which Appraiser Assistant operates, without regard to conflict of law principles."
        },
        {
          subtitle: "Dispute Resolution",
          text: "Any disputes arising from these terms shall be resolved through binding arbitration or in the courts of competent jurisdiction."
        }
      ]
    },
    {
      title: "12. Contact Information",
      content: [
        {
          subtitle: "Questions",
          text: "For questions about these Terms of Service, please contact us at support@appraiserassistant.com or visit our Contact Us page."
        }
      ]
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      <div className="relative pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div
            ref={headerRef}
            className={`text-center mb-12 transition-all duration-700 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              }`}
            >
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm text-gray-300">Legal agreement</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Terms of{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Service
              </span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Please read these terms carefully before using our services. By using Appraiser Assistant, you agree to these terms.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-12 shadow-2xl">
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={sectionIndex > 0 ? "mt-10" : ""}>
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-6">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <h3 className="text-cyan-400 font-semibold mb-2">{item.subtitle}</h3>
                        <p className="text-gray-300 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
